import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Store, Bike, MapPin, Calendar, ImageOff, Loader2, Share2, Printer, ArrowLeft, Star } from 'lucide-react';
import { ordersApi, ratingsApi } from '../services/api';
import { Button } from '../components/ui/button';
import RatingPrompt from '../components/RatingPrompt';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

/**
 * ReceiptPage — public, no-login delivery receipt.
 * Loads /api/orders/{id}/receipt/public?t=<signed_token>. All PII is redacted
 * server-side; only the buyer / vendor owner / driver / admin can mint the
 * signed URL that opens this page.
 */
const fmt = (v) => (v ? new Date(v).toLocaleString() : '—');
const money = (n, ccy = 'CAD') => `${ccy} ${Number(n || 0).toFixed(2)}`;

const ReceiptPage = () => {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const t = params.get('t') || '';
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [myRatings, setMyRatings] = useState(null); // null = unloaded, [] = loaded
  const { user } = useAuth();
  const isAuthenticated = Boolean(user);

  useEffect(() => {
    if (!t) { setState({ loading: false, data: null, error: 'Missing share token.' }); return; }
    let alive = true;
    (async () => {
      try {
        const data = await ordersApi.publicReceipt(orderId, t);
        if (alive) setState({ loading: false, data, error: null });
      } catch (e) {
        if (alive) setState({ loading: false, data: null, error: e?.response?.data?.detail || 'Failed to load receipt.' });
      }
    })();
    return () => { alive = false; };
  }, [orderId, t]);

  // Only signed-in buyers can rate — pull any pre-existing ratings so we
  // don't nag someone who already rated (e.g. re-opening the receipt link).
  useEffect(() => {
    if (!isAuthenticated) return;
    let alive = true;
    ratingsApi.forOrder(orderId).then((r) => { if (alive) setMyRatings(r.ratings || []); }).catch(() => {});
    return () => { alive = false; };
  }, [orderId, isAuthenticated]);

  const share = async () => {
    try {
      const url = window.location.href;
      if (navigator.share) {
        await navigator.share({ title: `ShipitNow receipt ${orderId}`, text: 'Delivery receipt', url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Receipt link copied to clipboard');
      }
    } catch (_e) { /* user canceled */ }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <ImageOff className="h-8 w-8 text-slate-400 mx-auto" />
          <h1 className="mt-3 text-xl font-semibold text-slate-900">Receipt unavailable</h1>
          <p className="mt-1 text-slate-500 text-sm">{state.error}</p>
          <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to ShipitNow
          </Link>
        </div>
      </div>
    );
  }

  const r = state.data;
  const ccy = r.currency || 'CAD';

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="receipt-page">
      <div className="max-w-2xl mx-auto px-4 pt-8 print:px-0 print:pt-0">
        <header className="flex items-center justify-between mb-4 print:hidden">
          <Link to="/" className="text-sm font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="receipt-print-btn">
              <Printer className="h-4 w-4 mr-1.5" />Print / Save PDF
            </Button>
            <Button size="sm" onClick={share} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="receipt-share-btn">
              <Share2 className="h-4 w-4 mr-1.5" />Share
            </Button>
          </div>
        </header>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Hero */}
          <div className="p-6 sm:p-8 bg-gradient-to-br from-emerald-50 to-white border-b border-slate-100">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[11px] font-semibold uppercase tracking-wider">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Delivered
                </div>
                <h1 className="mt-3 text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
                  Your ShipitNow receipt
                </h1>
                <p className="text-slate-500 mt-1 text-sm">Order · <span className="font-mono">{r.order_id}</span></p>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total paid</div>
                <div className="text-3xl font-semibold text-slate-900 mt-0.5" data-testid="receipt-total">{money(r.total, ccy)}</div>
              </div>
            </div>
          </div>

          {/* Meta */}
          <div className="p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Info icon={Store} label="Store" value={r.vendor_name || '—'} testid="receipt-vendor" />
            <Info icon={Bike} label="Delivered by" value={r.driver?.name || 'ShipitNow Driver'} testid="receipt-driver" />
            <Info icon={MapPin} label="Delivered to" value={r.address || '—'} />
            <Info icon={Calendar} label="Delivered at" value={fmt(r.delivered_at)} testid="receipt-delivered-at" />
          </div>

          {/* Items */}
          <div className="px-6 sm:px-8 pb-2">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Items</div>
            <ul className="text-sm divide-y divide-slate-100" data-testid="receipt-items">
              {(r.items || []).map((it, i) => (
                <li key={`${it.id || it.name}-${i}`} className="py-2 flex justify-between">
                  <span className="text-slate-800">{(it.qty || 1)} × {it.name}</span>
                  <span className="font-medium text-slate-900">{money((it.price || 0) * (it.qty || 1), ccy)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Totals */}
          <div className="px-6 sm:px-8 pt-4">
            <TotalRow label="Subtotal" value={money(r.subtotal, ccy)} />
            {r.tax != null && <TotalRow label="Tax" value={money(r.tax, ccy)} />}
            {r.delivery_fee != null && <TotalRow label="Delivery fee" value={money(r.delivery_fee, ccy)} />}
            {r.service_fee != null && <TotalRow label="Service fee" value={money(r.service_fee, ccy)} />}
            {r.tip != null && r.tip > 0 && <TotalRow label="Tip" value={money(r.tip, ccy)} />}
            <div className="flex justify-between py-3 border-t border-slate-200 mt-1">
              <span className="text-slate-900 font-semibold">Total</span>
              <span className="text-slate-900 font-semibold">{money(r.total, ccy)}</span>
            </div>
          </div>

          {/* POD photos */}
          <div className="px-6 sm:px-8 py-6 border-t border-slate-100">
            <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Proof of delivery</div>            {r.pod_skip_reason ? (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-start gap-2">
                <ImageOff className="h-4 w-4 mt-0.5" />
                <div>
                  <div className="font-semibold">Photos couldn't be captured</div>
                  <div className="mt-0.5">Driver's note: {r.pod_skip_reason}</div>
                </div>
              </div>
            ) : (r.pod_photos || []).length ? (
              <div className="grid grid-cols-2 gap-2" data-testid="receipt-pod-photos">
                {r.pod_photos.map((url, i) => {
                  const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
                  return (
                    <a key={url || i} href={fullUrl} target="_blank" rel="noreferrer" className="block aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200 hover:opacity-95">
                      <img src={fullUrl} alt={`POD ${i + 1}`} className="w-full h-full object-cover" />
                    </a>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-slate-500 italic">No photos on file.</div>
            )}
          </div>

          {/* Ratings — only when the reader is the signed-in buyer */}
          {isAuthenticated && myRatings !== null && (
            <div className="px-6 sm:px-8 py-6 border-t border-slate-100 space-y-3" data-testid="rating-section">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Rate this delivery
              </div>
              <RatingPrompt
                orderId={r.order_id}
                target={{ type: 'driver', name: r.driver?.name || 'your driver' }}
                alreadyRatedStars={myRatings.find((x) => x.target_type === 'driver')?.stars || null}
                alreadyComment={myRatings.find((x) => x.target_type === 'driver')?.comment || null}
                onRated={() => setMyRatings((prev) => [...(prev || []).filter((x) => x.target_type !== 'driver'), { target_type: 'driver' }])}
              />
              <RatingPrompt
                orderId={r.order_id}
                target={{ type: 'vendor', name: r.vendor_name || 'the store' }}
                alreadyRatedStars={myRatings.find((x) => x.target_type === 'vendor')?.stars || null}
                alreadyComment={myRatings.find((x) => x.target_type === 'vendor')?.comment || null}
                onRated={() => setMyRatings((prev) => [...(prev || []).filter((x) => x.target_type !== 'vendor'), { target_type: 'vendor' }])}
              />
            </div>
          )}

          {/* Footer */}
          <div className="px-6 sm:px-8 py-4 bg-slate-50 border-t border-slate-200 text-center">
            <div className="text-xs text-slate-500">
              Powered by <span className="font-semibold text-slate-700">ShipitNow</span> · Anything, anytime, delivered.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Info = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-start gap-3">
    <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none">
      <Icon className="h-4.5 w-4.5" />
    </div>
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm font-medium text-slate-900 mt-0.5" data-testid={testid}>{value}</div>
    </div>
  </div>
);

const TotalRow = ({ label, value }) => (
  <div className="flex justify-between text-sm py-1">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-800">{value}</span>
  </div>
);

export default ReceiptPage;
